// ─────────────────────────────────────────────────────────────────────────────
// Saisie et lecture des MONTANTS — logique PURE (aucun React, testable seule)
// ─────────────────────────────────────────────────────────────────────────────
// POURQUOI CE FICHIER EXISTE (bug prouve en prod le 22/08/2026) :
// saisie « 125 000 » -> base « 125.00 ». Cause : <input type="number">. Le
// navigateur applique sa sanitisation, le separateur de milliers rend la valeur
// invalide, et ce qui parvient a JavaScript est DEJA ampute. `Number("125 000")`
// vaut NaN, donc la troncature n'est PAS dans le parsing : elle est en amont.
// -> Le remede n'est pas de mieux parser : c'est de NE PLUS UTILISER type="number"
//    sur un montant, et de parser nous-memes une saisie texte brute.
//
// PIEGE PRINCIPAL — l'espace INSECABLE (U+00A0) et l'espace fine insecable
// (U+202F) : c'est ce que produisent Excel, LibreOffice, et `toLocaleString('fr-FR')`
// lui-meme. Autrement dit le format que NOUS affichons est precisement celui qui
// cassait la saisie au recollage. D'ou le test d'aller-retour format -> parse.

// Decimales par devise (ISO-4217). Les 7 devises couvertes par
// backend/src/config/pays.js, plus EUR/USD en prevision.
const DECIMALES_PAR_DEVISE = {
  XOF: 0,  // franc CFA Ouest (UEMOA)
  XAF: 0,  // franc CFA Centre (CEMAC)
  GNF: 0,  // franc guineen
  KMF: 0,  // franc comorien
  CDF: 2,  // franc congolais
  GHS: 2,  // cedi ghaneen
  NGN: 2,  // naira nigerian
  EUR: 2,
  USD: 2,
};

// ⚠️ DEFAUT A 2, PAS A 0 — exigence explicite. Supposer zero pour une devise
// inconnue ferait perdre les centimes SANS AUCUNE ALERTE le jour ou EUR ou USD
// arrivent. Une decimale affichee en trop se voit ; une decimale perdue, non.
const DECIMALES_DEFAUT = 2;

export function decimalesDevise(devise) {
  if (!devise) return DECIMALES_DEFAUT;
  const d = DECIMALES_PAR_DEVISE[String(devise).toUpperCase().trim()];
  return d === undefined ? DECIMALES_DEFAUT : d;
}

// TOUS les blancs qu'un copier-coller peut produire : espace ordinaire,
// INSECABLE (U+00A0), fine insecable (U+202F), fine (U+2009), figure (U+2007),
// et le joncteur invisible (U+2060). `\s` en couvre deja une partie : on les
// nomme quand meme, pour que la liste soit lisible et verifiable.
const BLANCS = /[\s    ⁠]/g;
const CARACTERES_AUTORISES = /^[0-9.,+-]+$/;
const NOMBRE_CANONIQUE = /^[0-9]+(\.[0-9]+)?$/;

/**
 * Analyse une saisie brute et renvoie { valeur, ok, erreur }.
 *
 *   valeur : number | null   (null = champ VIDE, jamais 0 — cf. doctrine
 *                             « prix non defini » du 19/08 : un 0 se lit comme
 *                             une prestation contractuellement OFFERTE)
 *   ok     : boolean
 *   erreur : string | null
 *
 * REGLE DE DESAMBIGUISATION du dernier separateur (`.` ou `,`) :
 *   - 3 chiffres apres  -> separateur de MILLIERS  ("125.000" et "125,000" = 125000)
 *   - 1 ou 2 chiffres   -> separateur DECIMAL      ("125,50" = 125.5)
 *   - 0 ou >3 chiffres  -> refus
 * ⚠️ ARBITRAGE ASSUME : « 125.000 » vaut donc 125000, jamais 125. C'est l'usage
 * francais et c'est la regle demandee. Consequence a connaitre : en devise a 2
 * decimales, quelqu'un qui ecrirait « 125.000 » en pensant « 125 euros » obtient
 * 125 000. Ecrire 3 decimales n'est de toute facon canonique dans aucune des
 * devises supportees.
 */
export function parseMontant(brut, decimales = DECIMALES_DEFAUT) {
  const VIDE = { valeur: null, ok: true, erreur: null };
  if (brut === null || brut === undefined) return VIDE;

  const nettoye = String(brut).replace(BLANCS, '');
  if (nettoye === '') return VIDE; // "" -> null, JAMAIS 0

  const KO = (erreur) => ({ valeur: null, ok: false, erreur });

  if (!CARACTERES_AUTORISES.test(nettoye)) return KO('Montant invalide');
  if (nettoye.startsWith('-')) return KO('Montant negatif refuse');

  const sansSigne = nettoye.replace(/^\+/, '');
  // Un signe ailleurs qu'en tete n'a aucun sens dans un montant.
  if (sansSigne.includes('-') || sansSigne.includes('+')) return KO('Montant invalide');
  if (!/[0-9]/.test(sansSigne)) return KO('Montant invalide');

  const dernierSep = Math.max(sansSigne.lastIndexOf('.'), sansSigne.lastIndexOf(','));
  let normalise;
  if (dernierSep === -1) {
    normalise = sansSigne;
  } else {
    const queue = sansSigne.slice(dernierSep + 1);
    if (queue.length === 3) {
      normalise = sansSigne.replace(/[.,]/g, '');                       // milliers
    } else if (queue.length === 1 || queue.length === 2) {
      const tete = sansSigne.slice(0, dernierSep).replace(/[.,]/g, ''); // milliers + decimal
      normalise = tete + '.' + queue;
    } else {
      return KO('Montant invalide');
    }
  }

  if (!NOMBRE_CANONIQUE.test(normalise)) return KO('Montant invalide');

  const valeur = Number(normalise);
  if (!Number.isFinite(valeur)) return KO('Montant invalide');

  const nbDecimales = normalise.includes('.') ? normalise.split('.')[1].length : 0;
  if (nbDecimales > decimales) {
    return KO(decimales === 0
      ? 'Cette devise n’a pas de decimales'
      : `Maximum ${decimales} decimale${decimales > 1 ? 's' : ''}`);
  }

  return { valeur, ok: true, erreur: null };
}

/** Affichage formate (lecture / apres blur). Chaine vide si pas de valeur. */
export function formatMontant(valeur, devise) {
  if (valeur === null || valeur === undefined || valeur === '') return '';
  const n = Number(valeur);
  if (!Number.isFinite(n)) return '';
  const d = decimalesDevise(devise);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d });
}

/**
 * Texte a mettre DANS le champ pendant l'edition : aucun separateur de milliers.
 * On n'edite jamais du texte formate — c'est ce qui deplace le curseur.
 */
export function texteEditable(valeur, devise) {
  if (valeur === null || valeur === undefined || valeur === '') return '';
  const n = Number(valeur);
  if (!Number.isFinite(n)) return '';
  const d = decimalesDevise(devise);
  return d === 0 ? String(Math.trunc(n)) : String(n).replace('.', ',');
}
